import { useEffect, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { PrimaryTitleGray } from '../../../app/theme/tokens/colors';
import { TextAreaInput } from '../../../shared/ui/Inputs';
import { OrderFlowTable } from '../../../shared/ui/orderFlowTable';
import { ImageSlider } from '../../../shared/media/slider';
import { saveAssistanceOrder } from '../../../services/technical-assistance/assistanceService';
import { getProductsImages } from '../../../services/products/productService';
import { usePortalStore } from '../../../stores/portalStore';
import { useTechnicalAssistanceStore } from '../store/technicalAssistanceStore';
import { DefaultFinishOrder } from '../../orders/usecases/finish/defaultFinishOrder';
import { getBase64Img } from '../../../utils/format';
import { genericOrderCode } from '../../../utils/orders';

export const FinishAssistanceOrder = () => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const currentUser = usePortalStore((state) => state.currentUser);
  const setCurrentAlertError = usePortalStore(
    (state) => state.setCurrentAlertError
  );
  const selectedEquipments = useTechnicalAssistanceStore(
    (state) => state.selectedEquipments
  );
  const selectedPatient = useTechnicalAssistanceStore(
    (state) => state.selectedPatient
  );
  const typedSearch = useTechnicalAssistanceStore((state) => state.typedSearch);
  const equipmentList = useTechnicalAssistanceStore(
    (state) => state.equipmentList
  );
  const selectedSearch = useTechnicalAssistanceStore(
    (state) => state.selectedSearch
  );
  const [techDetails, setTechDetails] = useState('');
  const [image, setImage] = useState([]);
  const navigate = useNavigate();

  const selectedEquipmentIds = Object.keys(selectedEquipments).filter(
    (id) => selectedEquipments[id] === true && id !== String(genericOrderCode)
  );

  const equipmentsWithoutSolution = selectedEquipmentIds.filter((id) => {
    const solutions = selectedSearch?.[id];
    return !Array.isArray(solutions) || solutions.length === 0;
  });

  const requiresProblemDetail = equipmentsWithoutSolution.length > 0;

  useEffect(() => {
    if (!selectedPatient?.cpf) {
      navigate('/technicalAssistance/assistanceToPatient');
    }
  }, [selectedPatient, navigate]);

  useEffect(() => {
    if (!selectedPatient?.cpf) return;

    (async () => {
      const codes = Object.keys(selectedEquipments).map((x) => x.toString());
      const imgs = await getProductsImages(codes);
      setImage([...imgs]);
    })();
  }, [selectedPatient?.cpf, selectedEquipments]);

  if (!selectedPatient?.cpf) {
    return null;
  }

  const finishOrder = async (deliveryDate, deliveryTurn) => {
    const saveTypedSearch = {};
    const normalizedTechDetails = String(techDetails ?? '').trim();

    Object.keys(selectedEquipments).forEach((eqId) => {
      if (selectedSearch[eqId] && typedSearch[eqId]) {
        saveTypedSearch[eqId] = typedSearch[eqId];
      }
    });

    if (requiresProblemDetail && normalizedTechDetails === '') {
      setCurrentAlertError(
        'É obrigatório detalhar o problema quando houver equipamento sem solução selecionada.'
      );
      return null;
    }

    if (Object.keys(typedSearch).length === 0 && normalizedTechDetails === '') {
      setCurrentAlertError(
        'Por favor, insira ao menos uma informação para continuar com o pedido.'
      );
      return null;
    }

    const orderNumber = await saveAssistanceOrder(
      currentUser,
      selectedPatient.cnpj,
      selectedPatient,
      selectedEquipments,
      deliveryDate,
      normalizedTechDetails,
      deliveryTurn,
      saveTypedSearch,
      selectedSearch
    );
    return orderNumber;
  };

  const getSelectedEquipments = () => {
    const eq = [];
    if (selectedEquipments[genericOrderCode]) {
      eq.push({
        id: [genericOrderCode],
        name: 'Validação e Processamento de Pedidos',
      });
      return eq;
    }

    Object.keys(selectedEquipments).forEach((k) => {
      if (selectedEquipments[k]) {
        const equipment = equipmentList.filter(
          (x) => x.id === parseInt(k, 10) || x.id === k
        )[0];
        eq.push(equipment);
      }
    });

    return eq;
  };

  const topTile = () => {
    return (
      <div style={{ display: isTabletOrMobile ? 'block' : 'inline-flex' }}>
        <div>
          <ImageSlider images={image.map((x) => getBase64Img(x))}></ImageSlider>
        </div>
        <div
          style={{ flex: '1', marginLeft: isTabletOrMobile ? '0px' : '40px' }}
        >
          <div>
            {getSelectedEquipments().map((eq, idx) => (
              <div key={idx} style={{ marginBottom: '10px' }}>
                <h4
                  style={{
                    marginTop: '0px',
                    color: 'black',
                    textTransform: 'uppercase',
                    fontSize: '18px',
                    fontWeight: '500',
                    borderBottom: 'solid 1px #a7a6a6',
                  }}
                >
                  {eq.name} {eq.model}
                </h4>
                {typedSearch && typedSearch[eq.id] ? (
                  <h5>Buscas: {typedSearch[eq.id].join(', ')}</h5>
                ) : (
                  <></>
                )}
                {selectedSearch[eq.id] ? (
                  <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                    <OrderFlowTable
                      ariaLabel="Soluções da assistência"
                      header={['Problema', 'Causa', 'Solução']}
                      data={(selectedSearch[eq.id] ?? [])
                        .map((x) => {
                          const c = { ...x };
                          delete c.genId;
                          return c;
                        })
                        .flat()}
                    ></OrderFlowTable>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const bottomTile = () => {
    return (
      <div style={{ width: '100%' }}>
        <h4
          style={{
            color: PrimaryTitleGray,
            margin: '0px',
            marginBottom: '10px',
          }}
        >
          Detalhar a ocorrência da solicitação
        </h4>
        {requiresProblemDetail ? (
          <p
            style={{
              margin: '0px 0px 10px 0px',
              color: '#b45309',
              fontSize: '13px',
              lineHeight: '18px',
            }}
          >
            Atenção: existem equipamentos sem solução selecionada. Descreva
            obrigatoriamente o problema para concluir o pedido.
          </p>
        ) : (
          <></>
        )}
        <TextAreaInput
          onChange={(e) => setTechDetails(e.target.value)}
          style={{ minWidth: '100%', minHeight: '120px', margin: '0px' }}
        ></TextAreaInput>
      </div>
    );
  };

  return (
    <DefaultFinishOrder
      selectedPatient={selectedPatient}
      topTileRender={topTile}
      bottomTileRender={bottomTile}
      orderType={'Assistência técnica'}
      onFinish={finishOrder}
      fastDeliveryLabel={'Assistência imediata'}
      scheduleDeliveryLabel={'Assistência agendada'}
    ></DefaultFinishOrder>
  );
};
